import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSessionIdleStore,
  sessionDeadlineStorageKey,
  sessionPolicyToConfig,
  SessionTimeoutBanner,
} from '@easyflow/ui-frontend';
import { sessionPolicySchema } from '@easyflow/shared';
import apiClient from '@/lib/api';
import ForceLogoutDialog from '@/components/ForceLogoutDialog';
import {
  performLogoutCleanup,
  initPageUnloadHandler,
  refreshToken as refreshAccessToken,
  type RefreshOutcome,
  TOKEN_REFRESHED_EVENT,
  SESSION_EXPIRED_EVENT,
} from '@/lib/tokenRefresh';
import { persistSessionPolicy, resolveSessionDurations } from '@/lib/sessionPolicyStorage';
import { AUTH_STORAGE_KEYS, migrateAuthStorage, clearAllAuthStorage } from '@/constants/auth';
import { syncNotificationPrefsToSW, clearNotificationPrefsSnapshot } from '@/lib/notificationPrefsSync';
import type { SessionPolicy } from '@easyflow/shared';
import type { SessionExpireEvent, SessionStartReason } from '@easyflow/ui-frontend';
import type { User, AuthState, AuthContextType } from '@/types/auth';

const SESSION_SCOPE = 'tenant';

const LOGGED_OUT_STATE: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
};

function decodeJwtExpMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload: unknown = JSON.parse(atob(parts[1]!));
    if (typeof payload !== 'object' || payload === null) return null;
    const exp: unknown = Reflect.get(payload, 'exp');
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function parseStoredUser(raw: string): User {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null || typeof Reflect.get(parsed, 'id') !== 'string') {
    throw new Error('Invalid stored user');
  }
  return parsed as User;
}

/** Deadline di inattività persistita dal motore condiviso (condivisa tra tab) già superata. */
function isPersistedIdleDeadlinePassed(): boolean {
  const raw = localStorage.getItem(sessionDeadlineStorageKey(SESSION_SCOPE));
  if (!raw) return false;
  const deadline = Number(raw);
  return Number.isFinite(deadline) && deadline <= Date.now();
}

export type { User, AuthState, AuthContextType };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const getTenantLoginPath = useCallback(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const slug = parts[0] || 'demo';
    return `/${slug}/login`;
  }, [location.pathname]);

  // Scadenza per inattività dal motore condiviso: la tab che l'ha rilevata per
  // prima revoca lato server, le altre puliscono solo lo stato locale; tutte
  // tornano alla login del tenant. Ref per non riavviare lo store a ogni render.
  const handleIdleExpireRef = useRef<(event: SessionExpireEvent) => void>(() => undefined);
  // Esito del rinnovo richiesto dal tick del motore: solo `expired` (401
  // definitivo o nessuna credenziale di refresh) termina la sessione.
  const handleRefreshOutcomeRef = useRef<(outcome: RefreshOutcome) => void>(() => undefined);

  // 'login' dopo autenticazione esplicita (deadline nuova), 'restore' al
  // bootstrap (deadline persistita adottata, mai reset artificiale).
  const sessionStartReasonRef = useRef<SessionStartReason>('restore');

  /**
   * Avvia (o riconfigura) l'unico orologio di inattività, scope `tenant`.
   * L'adapter fornisce solo callback e le durate della policy persistita: il
   * tracker attività, lo scheduler e la deadline cross-tab sono del core.
   */
  const startSessionClock = useCallback((reason: SessionStartReason) => {
    getSessionIdleStore(SESSION_SCOPE).start(
      {
        scope: SESSION_SCOPE,
        ...resolveSessionDurations(),
        getAccessToken: () => localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN),
        onRefreshNeeded: async () => {
          handleRefreshOutcomeRef.current(await refreshAccessToken());
        },
        onExpire: (event) => handleIdleExpireRef.current(event),
      },
      { reason },
    );
  }, []);

  useEffect(() => {
    migrateAuthStorage();
    let cancelled = false;

    const endSessionOnLoad = () => {
      void performLogoutCleanup();
      clearAllAuthStorage();
      localStorage.removeItem(sessionDeadlineStorageKey(SESSION_SCOPE));
      apiClient.clearToken();
      setAuthState(LOGGED_OUT_STATE);
    };

    const bootstrap = async () => {
      const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      const userData = localStorage.getItem(AUTH_STORAGE_KEYS.USER);

      if (!token || !userData) {
        apiClient.clearToken();
        setAuthState(LOGGED_OUT_STATE);
        return;
      }

      let user: User;
      const expMs = decodeJwtExpMs(token);
      try {
        if (expMs === null) throw new Error('Invalid token format');
        user = parseStoredUser(userData);
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
        localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
        apiClient.clearToken();
        setAuthState(LOGGED_OUT_STATE);
        return;
      }

      // La deadline di inattività è condivisa tra tab e sopravvive al reload:
      // se è già passata la sessione è finita, qualunque sia lo stato del JWT.
      if (isPersistedIdleDeadlinePassed()) {
        endSessionOnLoad();
        return;
      }

      let activeToken = token;
      if (expMs <= Date.now()) {
        // Access scaduto ma utente attivo di recente: si prova il refresh
        // prima di mandare al login. Un errore temporaneo non termina la
        // sessione (il motore ritenta al primo tick); solo il 401 definitivo.
        if (!localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)) {
          endSessionOnLoad();
          return;
        }
        const outcome = await refreshAccessToken();
        if (cancelled) return;
        if (outcome.kind === 'expired') {
          endSessionOnLoad();
          return;
        }
        if (outcome.kind === 'refreshed') {
          activeToken = outcome.token;
        }
      }

      apiClient.setToken(activeToken);
      sessionStartReasonRef.current = 'restore';
      setAuthState({
        user,
        token: activeToken,
        isAuthenticated: true,
        isLoading: false,
      });
    };

    void bootstrap();

    const handleUnauthorized = async () => {
      console.log('[Auth] Unauthorized event received - full cleanup + redirect');
      try {
        await performLogoutCleanup();
      } catch {}
      clearAllAuthStorage();
      apiClient.clearToken();
      setAuthState(LOGGED_OUT_STATE);
    };

    const handleSessionExpired = async () => {
      console.log('[Auth] Session expired - revoking refresh token and cleaning up');
      await performLogoutCleanup();
      clearAllAuthStorage();
      apiClient.clearToken();
      setAuthState(LOGGED_OUT_STATE);
    };

    const handleForceLogout = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Se in questa stessa tab è in corso un forceLogin (vedi
      // Login.tsx → handleForceLogin), il broadcast `session:force-logout`
      // emesso dal backend è il riflesso del nostro stesso login: lo deve
      // ricevere SOLO la sessione vecchia su altri dispositivi/browser,
      // non il browser che sta facendo il nuovo accesso. In quel caso
      // ignoriamo il singolo evento — la sessione locale è quella nuova,
      // appena creata, e non va terminata.
      if ((window as Window & { __edgvoip_login_in_progress?: boolean }).__edgvoip_login_in_progress) {
        console.log('[Auth] Ignoring session:force-logout - own forceLogin in progress');
        return;
      }
      console.log('[Auth] Force logout received - another device logged in');
      clearAllAuthStorage();
      apiClient.clearToken();
      setAuthState(LOGGED_OUT_STATE);
      window.dispatchEvent(new CustomEvent('session:force-logout-ui', { detail }));
    };

    const handleTokenRefreshed = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const detail: unknown = e.detail;
      if (typeof detail !== 'object' || detail === null) return;
      const newToken: unknown = Reflect.get(detail, 'token');
      if (typeof newToken !== 'string' || !newToken) return;
      setAuthState(prev => ({ ...prev, token: newToken }));
      // Policy aggiornata a caldo: una modifica dal master raggiunge la
      // sessione al primo rinnovo, senza relogin. Il rinnovo NON è attività:
      // il motore ricalcola la deadline dall'ultima attività reale.
      const sessionPolicy = sessionPolicySchema.safeParse(Reflect.get(detail, 'sessionPolicy'));
      if (sessionPolicy.success) {
        getSessionIdleStore(SESSION_SCOPE).updateConfig(sessionPolicyToConfig(sessionPolicy.data));
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === AUTH_STORAGE_KEYS.TOKEN && e.newValue === null) {
        console.log('[Auth] Token removed in another tab - syncing logout');
        clearAllAuthStorage();
        apiClient.clearToken();
        setAuthState(LOGGED_OUT_STATE);
        return;
      }
      // Policy rinnovata da un'altra tab: le durate devono cambiare QUI prima
      // della prossima attività locale, altrimenti questa tab riscriverebbe la
      // deadline condivisa con la vecchia durata.
      if (e.key === AUTH_STORAGE_KEYS.SESSION_POLICY) {
        getSessionIdleStore(SESSION_SCOPE).updateConfig(resolveSessionDurations());
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener('session:force-logout', handleForceLogout);
    window.addEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
    window.addEventListener('storage', handleStorage);
    return () => {
      cancelled = true;
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener('session:force-logout', handleForceLogout);
      window.removeEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    if (authState.token) {
      apiClient.setToken(authState.token);
    } else {
      apiClient.clearToken();
    }
  }, [authState.token]);

  useEffect(() => {
    initPageUnloadHandler();
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated) return;
    // Snapshot the user's notification preferences into IndexedDB so the
    // Service Worker can gate Web Push pre-render (per category, quiet
    // hours, silent flag). Failure is non-fatal — the SW falls back to
    // server-side gating. See packages/frontend/src/lib/notificationPrefsSync.ts.
    syncNotificationPrefsToSW().catch(() => { /* ignored */ });
  }, [authState.isAuthenticated]);

  /**
   * Termina la sessione locale. `revoke` = revoca anche il refresh token lato
   * server (sempre, tranne quando è già stato fatto da un'altra tab).
   */
  const endSession = useCallback(async (revoke: boolean) => {
    if (revoke) {
      try {
        await performLogoutCleanup();
      } catch (error) {
        console.error('[Auth] Logout cleanup failed:', error);
      }
    }
    clearAllAuthStorage();
    apiClient.clearToken();
    // Drop the SW's cached preference matrix so the next user (e.g. on a
    // shared device) doesn't inherit the previous user's quiet hours/silent
    // flags. Non-fatal if it fails.
    clearNotificationPrefsSnapshot().catch(() => { /* ignored */ });
    setAuthState(LOGGED_OUT_STATE);
  }, []);

  const logout = useCallback(() => endSession(true), [endSession]);

  handleIdleExpireRef.current = (event) => {
    void endSession(event.isInitiator).then(() => navigate(getTenantLoginPath()));
  };

  handleRefreshOutcomeRef.current = (outcome) => {
    // `transient` non termina nulla (il motore ritenta al prossimo tick).
    // `expired`: la revoca server-side è già avvenuta o non c'è nulla da
    // revocare (refresh token assente/consumato) → solo pulizia locale.
    if (outcome.kind !== 'expired') return;
    void endSession(false).then(() => navigate(getTenantLoginPath()));
  };

  // Unico orologio di inattività: dipende SOLO da isAuthenticated. Il refresh
  // automatico cambia il token ma non deve riavviare né ringiovanire la sessione.
  useEffect(() => {
    if (!authState.isAuthenticated) return;
    startSessionClock(sessionStartReasonRef.current);
    sessionStartReasonRef.current = 'restore';
    return () => getSessionIdleStore(SESSION_SCOPE).stop();
  }, [authState.isAuthenticated, startSessionClock]);

  const login = async (
    token: string,
    userData?: Partial<User>,
    refreshTokenValue?: string,
    sessionPolicy?: SessionPolicy,
  ) => {
    try {
      if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(token.split('.')[1]!));
      const user: User = {
        id: payload.id || payload.userId,
        email: payload.email,
        firstName: userData?.firstName || payload.firstName || '',
        lastName: userData?.lastName || payload.lastName || '',
        tenantId: payload.tenant_id || payload.tenantId,
        tenant_id: payload.tenant_id || payload.tenantId,
        tenantSlug: payload.role === 'super_admin' ? 'edgvoip' : (payload.tenant_slug || payload.tenantSlug),
        role: payload.role,
        extension: payload.extension || null,
        avatarUrl: userData?.avatarUrl || null,
        iat: payload.iat,
        exp: payload.exp,
      };

      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
      if (refreshTokenValue) {
        localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshTokenValue);
      }
      if (user.tenantSlug) {
        localStorage.setItem(AUTH_STORAGE_KEYS.TENANT_SLUG, user.tenantSlug);
      }
      let activeToken = token;
      if (sessionPolicy) {
        persistSessionPolicy(sessionPolicy);
      } else {
        // Nessuna policy nel payload (SSO: i token viaggiano nel fragment):
        // mai ereditare quella di una sessione precedente; la policy del
        // tenant arriva con il primo refresh, fatto subito, prima di avviare
        // l'orologio. Errore temporaneo → fallback di default fino al
        // prossimo rinnovo.
        localStorage.removeItem(AUTH_STORAGE_KEYS.SESSION_POLICY);
        if (refreshTokenValue) {
          const outcome = await refreshAccessToken();
          if (outcome.kind === 'expired') {
            throw new Error('Refresh token rejected at login');
          }
          if (outcome.kind === 'refreshed') {
            activeToken = outcome.token;
          }
        }
      }

      apiClient.setToken(activeToken);

      // Il login è attività: nuova deadline piena. Se lo store è già avviato
      // (relogin senza passare da logout) l'effetto non rieseguirebbe.
      if (authState.isAuthenticated) {
        startSessionClock('login');
      } else {
        sessionStartReasonRef.current = 'login';
      }
      setAuthState({
        user,
        token: activeToken,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('Invalid token:', error);
      clearAllAuthStorage();
      apiClient.clearToken();
      throw new Error('Invalid token');
    }
  };

  const refreshToken = async () => {
    if (!authState.token) return false;

    try {
      if (authState.token.split('.').length !== 3) {
        logout();
        return false;
      }

      const payload = JSON.parse(atob(authState.token.split('.')[1]!));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        // Token scaduto: verifica se c'è un refresh token disponibile prima di fare logout
        const hasRefreshToken = !!localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
        if (!hasRefreshToken) {
          logout();
          return false;
        }
        // Il motore di sessione condiviso rinnova il token; non fare logout ora
        return true;
      }
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  const updateUser = (updates: Partial<User>) => {
    if (!authState.user) return;
    
    const updatedUser = { ...authState.user, ...updates };
    localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(updatedUser));
    setAuthState(prev => ({
      ...prev,
      user: updatedUser
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        refreshToken,
        updateUser,
      }}
    >
      {children}
      {authState.isAuthenticated && (
        <SessionTimeoutBanner
          scope={SESSION_SCOPE}
          onKeepAlive={() => refreshAccessToken()}
          onLogout={async () => {
            await logout();
            navigate(getTenantLoginPath());
          }}
        />
      )}
      <ForceLogoutDialog onConfirm={() => navigate(getTenantLoginPath())} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Optional counterpart used by scoped, authenticated UI state. Some
 * standalone page tests render those pages without the application provider;
 * returning null keeps those pages usable while the real app still gets the
 * current user/tenant identity.
 */
export function useAuthOptional(): AuthContextType | null {
  return useContext(AuthContext) ?? null;
}
